import React from 'react';
import { ArrowDownRight, Github, Linkedin, Mail, ArrowDown } from 'lucide-react';
import { portfolioData } from '../mock';

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-24 pb-16 px-5 sm:px-8 lg:px-12 overflow-hidden"
    >
      {/* dotted backdrop */}
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left column — text */}
          <div className="lg:col-span-8 order-2 lg:order-1 space-y-9">
            <div
              className="section-label"
              style={{ animation: 'fade-in 700ms ease 100ms both' }}
            >
              <span>portfolio / v2</span>
            </div>

            <h1
              className="display text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] xl:text-[5.75rem]"
              style={{ animation: 'fade-up 800ms cubic-bezier(0.22,1,0.36,1) 180ms both' }}
            >
              Hi, I'm{' '}
              <span className="italic text-[hsl(var(--accent))]">Tharak</span>.
              <br />
              I build things with code,
              <br />
              models, and a lot of coffee
              <span className="text-[hsl(var(--accent))] caret" />
            </h1>

            <p
              className="text-xl sm:text-2xl text-[hsl(var(--foreground-muted))] max-w-2xl leading-snug"
              style={{ animation: 'fade-up 800ms cubic-bezier(0.22,1,0.36,1) 360ms both' }}
            >
              {portfolioData.personal.tagline}{' '}
              <span className="text-[hsl(var(--foreground))]">{portfolioData.personal.intro}</span>
            </p>

            <div
              className="flex flex-wrap items-center gap-4 pt-1"
              style={{ animation: 'fade-up 800ms cubic-bezier(0.22,1,0.36,1) 540ms both' }}
            >
              <button
                data-testid="hero-cta-work"
                onClick={() => scrollTo('experience')}
                className="btn-mono"
              >
                see the work <ArrowDownRight className="w-4 h-4" />
              </button>
              <a
                data-testid="hero-cta-resume"
                href={portfolioData.personal.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-mono btn-mono-accent"
              >
                resume
              </a>
            </div>

            <div
              className="flex flex-wrap items-center gap-5 pt-3"
              style={{ animation: 'fade-up 800ms cubic-bezier(0.22,1,0.36,1) 700ms both' }}
            >
              <a
                data-testid="hero-github"
                href={portfolioData.personal.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent))] transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                data-testid="hero-linkedin"
                href={portfolioData.personal.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent))] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                data-testid="hero-email"
                href={`mailto:${portfolioData.personal.email}`}
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent))] transition-colors"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
              <span className="mono text-[0.72rem] text-[hsl(var(--foreground-faint))] pl-1">
                available · open to roles in ml & research
              </span>
            </div>
          </div>

          {/* Right column — feature portrait card */}
          <aside
            className="lg:col-span-4 order-1 lg:order-2 flex justify-center lg:justify-end"
            style={{ animation: 'fade-in 1100ms ease 400ms both' }}
          >
            <div className="relative w-full max-w-[280px] sm:max-w-[320px] lg:max-w-none">
              {/* terracotta corner accent */}
              <div
                aria-hidden="true"
                className="absolute -top-3 -left-3 w-12 h-12 border-l-2 border-t-2 border-[hsl(var(--accent))] rounded-tl-sm pointer-events-none"
              />
              <div
                aria-hidden="true"
                className="absolute -bottom-3 -right-3 w-12 h-12 border-r-2 border-b-2 border-[hsl(var(--accent))] rounded-br-sm pointer-events-none"
              />

              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--rule))] rounded-sm p-4 sm:p-5 shadow-sm">
                {/* monospace caption strip */}
                <div className="flex items-center justify-between mono text-[0.65rem] text-[hsl(var(--foreground-faint))] uppercase tracking-wider mb-3">
                  <span>// profile</span>
                  <span className="text-[hsl(var(--accent))]">●</span>
                </div>

                {/* Portrait */}
                <div className="relative overflow-hidden rounded-sm bg-[hsl(var(--background-soft))]">
                  <img
                    src="images/profile.jpg"
                    alt="Tharak Koneni"
                    data-testid="hero-portrait"
                    loading="eager"
                    decoding="async"
                    className="block w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
                  />
                  {/* faint warm grade for cohesion in light mode */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 mix-blend-multiply pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, transparent 55%, hsl(var(--accent) / 0.08) 100%)',
                    }}
                  />
                </div>

                {/* Identity strip */}
                <div className="pt-4 pb-1">
                  <div className="display text-xl leading-tight">
                    {portfolioData.personal.name}
                  </div>
                  <div className="mono text-[0.7rem] text-[hsl(var(--foreground-faint))] mt-1 uppercase tracking-wider">
                    {portfolioData.personal.title} · researcher
                  </div>
                </div>

                {/* small meta rows */}
                <div className="mt-4 pt-4 border-t border-[hsl(var(--rule))] grid grid-cols-1 gap-2.5 mono text-[0.72rem]">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[hsl(var(--foreground-faint))]">currently</span>
                    <span className="text-[hsl(var(--foreground))] text-right">
                      USF · Graduate Researcher
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[hsl(var(--foreground-faint))]">focus</span>
                    <span className="text-[hsl(var(--foreground))] text-right">
                      LLMs · MLOps
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[hsl(var(--foreground-faint))]">contact</span>
                    <a
                      href={`mailto:${portfolioData.personal.email}`}
                      className="text-[hsl(var(--foreground))] link-static text-right break-all"
                    >
                      {portfolioData.personal.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollTo('about')}
          className="hidden md:flex items-center gap-2 mono text-[0.7rem] text-[hsl(var(--foreground-faint))] hover:text-[hsl(var(--accent))] transition-colors mt-20"
        >
          <span>scroll</span>
          <ArrowDown className="w-3 h-3 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
